import { getUserProfileImageUrl, UserIdentityModel } from "client/Api/UserProfile/service";
import {
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import {
	createValidatorForNewPost,
	EMPTY_POST_FORM_DATA,
	PostComposerFormData
} from "./common";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	BusyIndicator,
	IconButton,
	initialMinimalEditorValue,
	PersonAvatar,
	RichTextEditorIntermediateStateHandlers,
	useRichTextEditorIntermediateState,
	ValidationError
} from "client/Components";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useIsTouchDevice,
	useWarnBeforeUnload,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	EMPTY_ARRAY,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { UnmanagedComposerDialog } from "./UnmanagedComposerDialog";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { CreatePostPreview } from "./CreatePostPreview";
import { CommunityPostComposerFooter } from "./CommunityPostComposerFooter";
import { CommunityPostComposerMessage, CommunityPostComposerMessageHandle } from "./CommunityPostComposerMessage";
import { useIsEmpty } from "./useIsEmpty";
import { useSavePost } from "./useSavePost";
import styles from "./NewPostComposer.module.less";
import classNames from "classnames";
import { CommunityPostDetailsViewModel } from "client/Api/Communities/communityPostViewer";
import { CommunityViewModel } from "client/Api/Communities/communityPosts";
import { htmlToPlainText } from "client/Misc/htmlUtils";
import { PollEditor } from "./PollEditor";
import { Separator } from "./Separator";
import { CommunityPostComposerImages } from "./CommunityPostComposerImages";
import { useUploadCommunityPostImage } from "./useUploadCommunityPostImage";
import { DocumentLocationViewModel } from "client/Api/Hub/hubSettings";
import { CommunityPostComposerLinkedDocuments } from "./CommunityPostComposerLinkedDocuments";
import { documentsConfiguration, WebDavConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { useAddDocuments } from "../../useAddDocuments";
import { IValueInputAttachable } from "client/Forms/attachable";
import { ObjectWithClientId, setClientId } from "client/Misc/clientId";
import { LinkedDocumentFormData } from "../../common";

export const MAX_LINKED_DOCUMENTS_COUNT: number = 10;

export type NewPostComposerProps = {
	communityId: number | null;
	communities: readonly CommunityViewModel[];
	options: PostComposerOptions;
	author: UserIdentityModel;
	onAfterSave?: (post: CommunityPostDetailsViewModel) => void;
};

export const NewPostComposer = (props: NewPostComposerProps): ReactElement => {
	const {
		communityId,
		communities,
		options,
		author,
		onAfterSave
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [formData, setFormData] = useState<PostComposerFormData>(
		() => ({ ...EMPTY_POST_FORM_DATA, communityId })
	);
	const validationConfiguration = useMemo(
		(): IValidationConfiguration<PostComposerFormData> => createValidatorForNewPost(options),
		[options]
	);

	const [shouldShowMessageToolbar, setShouldShowMessageToolbar] = useState<boolean>(false);

	const alertManager: AlertManager = useAlerts();
	const messageRef: RefObject<CommunityPostComposerMessageHandle | null> = useRef<CommunityPostComposerMessageHandle>(null);

	const formHandler: IFormHandler<PostComposerFormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const editorHandlers: RichTextEditorIntermediateStateHandlers = useRichTextEditorIntermediateState(
		initialMinimalEditorValue
	);

	const { intermediateState } = editorHandlers;

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const isTouch: boolean = useIsTouchDevice();

	const isEmpty: boolean = useIsEmpty(formData, intermediateState);

	const { initials, avatarUrl } = useMemo(
		(): { initials: string; avatarUrl: string | null; } => {
			const { initials } = getComputedDisplayName({
				firstName: author.firstName,
				lastName: author.lastName
			});

			const avatarUrl: string | null = author.avatarId
				? getUserProfileImageUrl(author.avatarId)
				: null;

			return { initials, avatarUrl };
		},
		[author]
	);

	const messageAsPlainText = useMemo(
		(): string => {
			const message: string = formData.message;

			return htmlToPlainText(message);
		},
		[formData.message]
	);

	const shouldWarnBeforeExit: boolean = !isEmpty;

	const {
		isSharepointEnabled,
		webDavConfiguration
	} = useMemo(
		(): ({
			isSharepointEnabled: boolean;
			webDavConfiguration: WebDavConfiguration | null;
		}) => {
			const {
				sharePoint,
				webDav
			} = documentsConfiguration();

			return {
				isSharepointEnabled: sharePoint.isConfigured,
				webDavConfiguration: webDav
			};
		},
		[]
	);

	const linkedDocumentsAttachable: IValueInputAttachable<readonly ObjectWithClientId<LinkedDocumentFormData>[]> = formHandler
		.for("linkedDocuments");

	const linkedDocuments = useMemo(
		(): readonly ObjectWithClientId<LinkedDocumentFormData>[] => linkedDocumentsAttachable
			.value,
		[linkedDocumentsAttachable.value]
	);

	const selectionMadeHandler = useCallback(
		(itemsToAdd: readonly LinkedDocumentFormData[]) => {
			const currentItemsCount: number = linkedDocuments.length;
			const itemsToAddCount: number = itemsToAdd.length;

			if (itemsToAddCount + currentItemsCount > MAX_LINKED_DOCUMENTS_COUNT) {
				throw new Error(`The maximum number of allowed documents is ${MAX_LINKED_DOCUMENTS_COUNT}.`);
			}

			linkedDocumentsAttachable
				.reducer
				.setValue([
					...linkedDocuments,
					...itemsToAdd.map(setClientId)
				]);

			linkedDocumentsAttachable
				.validation
				.triggerInputFinished();
		},
		[
			linkedDocuments,
			linkedDocumentsAttachable.reducer,
			linkedDocumentsAttachable.validation
		]
	);

	const {
		add: addLinkedDocumentHandler,
		dialogElement: addLinkedDocumentDialog,
		openDocument: openLinkedDocument
	} = useAddDocuments({
		isSharepointEnabled,
		items: linkedDocuments,
		maxItemsCount: MAX_LINKED_DOCUMENTS_COUNT,
		onChangeIsLoading: setIsBusy,
		onSelect: selectionMadeHandler,
		webDavConfiguration
	});

	useWarnBeforeUnload(shouldWarnBeforeExit);

	const {
		onSelectImages,
		dialogElement: imageDialog
	} = useUploadCommunityPostImage(
		formHandler,
		setIsBusy,
		options,
		alertManager
	);

	const savePost: () => Promise<TryResult<CommunityPostDetailsViewModel>> = useSavePost(
		isBusy,
		setIsBusy,
		formHandler,
		isMobile
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<CommunityPostDetailsViewModel> = await savePost();

			if (!result.wasSuccessful) {
				return;
			}

			setFormData({...EMPTY_POST_FORM_DATA, communityId});
			setIsExpanded(false);
			onAfterSave?.(result.result);
		},
		[
			savePost,
			onAfterSave,
			communityId
		]
	);

	const focusEditorHandler = useCallback(
		(): void => {
			messageRef.current
				?.focus();
		},
		[]
	);

	const expandHandler = (): void => {
		if (isExpanded) {
			return;
		}

		setIsExpanded(true);

		setTimeout(
			(): void => {
				focusEditorHandler();
			},
			0
		);
	};

	const {
		onChange: onChangeEditorState,
		value: editorState,
		onBlur: onBlurEditor
	} = formHandler.for("message")
		.attachToRichTextEditor(editorHandlers);

	const renderMessageToolbar = (): ReactElement | null => {
		if(!shouldShowMessageToolbar) {
			return null;
		}

		return (
			<CommunityPostComposerFooter.Toolbar
				onChangeEditorState={onChangeEditorState}
				editorState={editorState}
				onFocusEditor={focusEditorHandler}
			/>
		);
	};

	const { poll } = formHandler.value;
	const hasPoll: boolean = poll.isSet && isNotNil(poll.value);
	const shouldShowDialog: boolean = isExpanded && isMobile;
	const shouldShowInlineEditor: boolean = isExpanded && !shouldShowDialog;
	const isInvalid: boolean = formHandler.validation
		.getValidationState()
		.kind === InputValidationResultKind.Invalid;

	const isWebDavAvailable: boolean = isNotNil(webDavConfiguration)
		&& webDavConfiguration.isConfigured;

	const canAddLinkedDocuments: boolean = linkedDocuments.length < MAX_LINKED_DOCUMENTS_COUNT;

	return (
		<>
			<div {...setTrackingId("new-post-composer")}>
				<div
					className={
						classNames(
							styles.root,
							{
								[styles.root_expanded]: isExpanded,
								[styles.root_hasError]: isInvalid
							}
						)
					}
					onClick={expandHandler}
				>
					<div
						className={styles.contentArea}
						{...setTrackingId("content-area")}
					>
						<PersonAvatar
							trackingId="avatar"
							imageSrc={avatarUrl}
							initials={initials}
						/>
						<div
							className={styles.messageContainer}
						>
							{shouldShowInlineEditor
								? (
									<CommunityPostComposerMessage
										ref={messageRef}
										value={editorState}
										onChange={onChangeEditorState}
										onBlur={onBlurEditor}
										onFocus={() => setShouldShowMessageToolbar(true)}
									/>
								)
								: (
									<CreatePostPreview messageText={messageAsPlainText} />
								)
							}
						</div>
						{shouldShowInlineEditor && (
							<IconButton
								icon="chevron-up"
								kind="transparent"
								shape="round"
								bubbleClick={false}
								onClick={() => setIsExpanded(false)}
								trackingId="collapse"
							/>
						)}
					</div>

					{shouldShowInlineEditor && (
						<>
							<ValidationError {...formHandler.for("message").attachToValidationError()} />

							{formHandler
								.for("poll")
								.when(
									x => x.isSet && isNotNil(x.value),
									handler => (
										<>
											<Separator />
											<PollEditor
												attachable={handler}
												onFocus={() => setShouldShowMessageToolbar(false)}
											/>
										</>
									)
								)
							}

							{formHandler.for("images")
								.when(
									x => x.length > 0,
									attachable => (
										<>
											<Separator />
											<CommunityPostComposerImages
												attachable={attachable}
												initialImages={EMPTY_ARRAY}
											/>
										</>
									)
								)
							}

							{formHandler.for("linkedDocuments")
								.when(
									x => x.length > 0,
									attachable => (
										<>
											<Separator />
											<CommunityPostComposerLinkedDocuments
												attachable={attachable}
												isTouch={isTouch}
												isMobile={isMobile}
												isSharepointEnabled={isSharepointEnabled}
												isWebDavAvailable={isWebDavAvailable}
												onOpenDocument={openLinkedDocument}
											/>
										</>
									)
								)
							}

							<CommunityPostComposerFooter
								attachable={formHandler}
								onSubmit={saveHandler}
								onSelectImages={onSelectImages}
								communities={communities}
								initialCommunityId={communityId}
								isBusy={isBusy}
								toolbar={renderMessageToolbar()}
								hasPoll={hasPoll}
								canAddLinkedDocuments={canAddLinkedDocuments}
								onAddLinkedDocument={addLinkedDocumentHandler}
							/>

							{isBusy && (
								<BusyIndicator />
							)}
						</>
					)}
				</div>
			</div>

			{shouldShowDialog && (
				<UnmanagedComposerDialog
					attachable={formHandler}
					options={options}
					isMobile={isMobile}
					isTouch={isTouch}
					isBusy={isBusy}
					onIsBusyChange={setIsBusy}
					mode="create"
					onClose={() => setIsExpanded(false)}
					onChangeEditorState={onChangeEditorState}
					onBlurEditor={onBlurEditor}
					editorState={editorState}
					onSubmit={saveHandler}
					communities={communities}
					initialCommunityId={communityId}
					shouldShowMessageToolbar={shouldShowMessageToolbar}
					onShouldShowMessageToolbarChange={setShouldShowMessageToolbar}
					isSharepointEnabled={isSharepointEnabled}
					isWebDavAvailable={isWebDavAvailable}
					canAddLinkedDocuments={canAddLinkedDocuments}
					onAddLinkedDocument={addLinkedDocumentHandler}
					onOpenLinkedDocument={openLinkedDocument}
				/>
			)}
			{imageDialog}
			{addLinkedDocumentDialog}
		</>
	);
};
NewPostComposer.displayName = "NewPostComposer";

export type PostComposerOptions = {
	supportedImageTypes: readonly string[];
	maxImageSize: number;
	defaultImageLocation: DocumentLocationViewModel | null;
	supportedImageExtensions: readonly string[];
};
